import { AfterViewInit, Component, Input, OnDestroy } from '@angular/core';
import * as echarts from 'echarts';
import { ConfigService } from '../../shared-services/config.service';
import { auditTime, filter, Subject, takeUntil } from 'rxjs';
import { GlobalService } from '../../shared-services/global.service';
import { DataErrorComponent } from '../../global-components/data-error/data-error.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

type AxisKey = 'x' | 'y';

interface AxisBreakdown {
  quarterLabels: string[];
  factorNames: string[];
  valuesByFactor: number[][];
  totals: number[];
}
@Component({
  selector: 'app-bubble-chart',
  imports: [DataErrorComponent, CommonModule, FormsModule],
  standalone: true,
  templateUrl: './bubble-chart.component.html',
  styleUrl: './bubble-chart.component.scss',
})
export class BubbleChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;
  public dynamicTitle = '';
  private myChart!: echarts.ECharts;
  public hasError = false;
  public errorMessage = '';
  private unSubscribe$: Subject<void> = new Subject();
  private chartData: any = null;
  private sourceChartData: any[] = [];
  filters: any;
  selectedFilter = 'all';
  private readonly factorColorMap: Record<string, string> = {
    KBF: '#0f5b78',
    'Lead time': '#e6782e',
    'Quality score': '#2e7d32',
    'Revenue score': '#0f5b78',
    'Profitability score': '#e6782e',
    'Aftmkt score': '#2e7d32',
    'Product cost score': '#1f9fd3',
  };
  private readonly fallbackPalette = ['#0f5b78', '#e6782e', '#2e7d32', '#1f9fd3', '#6b7280'];
  constructor(
    private configService: ConfigService,
    private filterState: GlobalService
  ) { }

  private debugLog(stage: string, data?: any): void {
    if (data !== undefined) {
      console.log(`[BubbleChart] ${stage}`, data);
      return;
    }
    console.log(`[BubbleChart] ${stage}`);
  }

  private destroy$ = new Subject<void>();

  ngAfterViewInit() {
    this.dynamicTitle = this.data?.title || 'Spend Effectiveness';
    this.filterState.filters$
      .pipe(
        // short delay to collect rapid emissions
        auditTime(1000),
        filter((filters) => filters !== null && filters !== undefined),
        takeUntil(this.destroy$)
      )
      .subscribe((filters) => {
        if (filters?.widgetId === this.data.widgetId) return;
        this.filters = filters.filter;
        this.updateDynamicTitleFromFilters(this.filters);
        this.loadAndRenderChart(filters.filter);
      });
  }

  private updateDynamicTitleFromFilters(filters: any): void {
    const quarterCandidates = [
      ...(Array.isArray(filters?.quarter_year) ? filters.quarter_year : []),
      ...(Array.isArray(filters?.start_quarter) ? filters.start_quarter : []),
      ...(Array.isArray(filters?.['end+quarter']) ? filters['end+quarter'] : []),
      ...(Array.isArray(filters?.end_quarter) ? filters.end_quarter : []),
    ];

    const normalized = Array.from(
      new Set(
        quarterCandidates
          .map((value: any) => this.parseQuarterYear(value))
          .filter((item): item is { quarter: number; year: number } => !!item)
      )
    );

    if (!normalized.length) {
      this.dynamicTitle = this.data?.title || 'Spend Effectiveness';
      return;
    }

    const sorted = [...normalized].sort(
      (a, b) => a.year - b.year || a.quarter - b.quarter
    );
    const start = sorted[0];
    const end = sorted.at(-1) || start;

    this.dynamicTitle = `Spend Effectiveness between ${this.formatQuarterTitle(
      start
    )} and ${this.formatQuarterTitle(end)}`;
  }

  private parseQuarterYear(value: any): { quarter: number; year: number } | null {
    if (value === null || value === undefined) return null;
    const normalized = String(value).replaceAll(/\s+/g, '').toUpperCase();
    const match = /^Q([1-4])(\d{2}|\d{4})$/.exec(normalized);
    if (!match) return null;

    const quarter = Number(match[1]);
    const yearRaw = match[2];
    const year = yearRaw.length === 2 ? Number(`20${yearRaw}`) : Number(yearRaw);
    if (!Number.isFinite(quarter) || !Number.isFinite(year)) return null;

    return { quarter, year };
  }

  private formatQuarterTitle(value: { quarter: number; year: number }): string {
    return `Q${value.quarter} ${value.year}`;
  }

  private loadAndRenderChart(filters?: any): void {
    const chartDom = document.getElementById(this.data?.widgetId);
    if (!chartDom) return;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom);
    }
    this.myChart.showLoading();
    this.selectedFilter = 'all';
    const requestConfig = {
      ...this.data,
      isBubbleChart: true,
    };
    this.configService
      .getApi(this.data.apiUrl, this.data.functionName, filters, requestConfig)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (res) => {
          this.debugLog('Raw API response', res);
          const responseData = this.extractResponseData(res);
          this.debugLog('Extracted responseData', responseData);
          const fallbackData = Array.isArray(this.data?.data) ? this.data.data : [];
          const normalized = this.normalizeChartData(
            responseData.length ? responseData : fallbackData
          );
          this.debugLog('Normalized chart data', normalized);

          const isEmpty = !normalized.length;
          if (isEmpty) {
            this.hasError = true;
            this.errorMessage = 'No data found for the selected filters.';
            this.myChart.clear();
            this.myChart.hideLoading();
            return;
          }

          this.hasError = false;
          this.sourceChartData = normalized;
          this.applyFilter(this.selectedFilter);
          this.addResizeListener();
        },
        error: () => {
          const fallbackData = this.normalizeChartData(this.data?.data || []);
          if (fallbackData.length) {
            this.hasError = false;
            this.sourceChartData = fallbackData;
            this.applyFilter(this.selectedFilter);
            this.addResizeListener();
            return;
          }

          this.hasError = true;
          this.errorMessage = 'No Data available for the chart.';
          this.myChart?.clear();
          this.myChart.hideLoading();
        },
      });
  }

  private extractResponseData(res: any): any[] {
    if (!res) return [];
    const responseKey = this.data?.responseKey;
    const responseList = Array.isArray(res?.responseList)
      ? res.responseList
      : Array.isArray(res?.[0]?.responseList)
        ? res[0].responseList
        : [];

    if (responseKey) {
      const responseListEntryByKey = responseList.find((entry: any) =>
        Array.isArray(entry?.[responseKey])
      );
      if (Array.isArray(responseListEntryByKey?.[responseKey])) {
        this.debugLog(`Mapped from responseList[*].${responseKey}`);
        return responseListEntryByKey[responseKey];
      }
    }

    const responseListEntry = responseList.find((entry: any) =>
      Array.isArray(entry?.responseData)
    );
    if (Array.isArray(responseListEntry?.responseData)) {
      this.debugLog('Mapped from responseList[*].responseData');
      return responseListEntry.responseData;
    }

    if (responseKey && Array.isArray(res?.[responseKey])) {
      this.debugLog(`Mapped from root.${responseKey}`);
      return res[responseKey];
    }

    if (responseKey && Array.isArray(res?.[0]?.[responseKey])) {
      this.debugLog(`Mapped from root[0].${responseKey}`);
      return res[0][responseKey];
    }

    if (Array.isArray(res?.responseData)) {
      this.debugLog('Mapped from root.responseData');
      return res.responseData;
    }

    if (Array.isArray(res?.[0]?.responseData)) {
      this.debugLog('Mapped from root[0].responseData');
      return res[0].responseData;
    }

    this.debugLog('Mapped from root array fallback');
    return Array.isArray(res) ? res : [];
  }

  private normalizeChartData(data: any[]): any[] {
    if (!Array.isArray(data)) return [];

    return data.map((item: any) => ({
      ...item,
      x: this.toNumber(item?.x),
      y: this.toNumber(item?.y),
      label: this.getPointLabel(item),
    }));
  }

  filterData() {
    const data = this.sourceChartData;
    if (!Array.isArray(data) || !data.length) return [];
    if (!this.filters) return data;

    const selectedPlatforms = Array.isArray(this.filters.final_em)
      ? this.filters.final_em
      : [];
    const selectedSegments = Array.isArray(this.filters.cleaned_vertical)
      ? this.filters.cleaned_vertical
      : [];

    if (!selectedPlatforms.length && !selectedSegments.length) {
      return data;
    }

    return data.filter((item: any) => {
      const label = this.getPointLabel(item);
      const emAll = !selectedPlatforms.length || selectedPlatforms.includes('All');
      const verticalAll = !selectedSegments.length || selectedSegments.includes('All');

      let em = '';
      let vertical = '';

      const emMatch = selectedPlatforms.find((e: string) =>
        this.includesIgnoreCase(label, e)
      );
      const verticalMatch = selectedSegments.find((v: string) =>
        this.includesIgnoreCase(label, v)
      );

      if (emMatch) em = emMatch;
      if (verticalMatch) vertical = verticalMatch;

      if (emAll && verticalAll) {
        return true;
      } else if (emAll) {
        return selectedSegments.includes(vertical);
      } else if (verticalAll) {
        return selectedPlatforms.includes(em);
      } else {
        return selectedPlatforms.includes(em) && selectedSegments.includes(vertical);
      }
    });
  }



  applyFilter(type: string) {
    this.selectedFilter = type;
    let filteredData = this.filterData();
    if (type === 'lt50') {
      filteredData = filteredData.filter((d: { x: number; }) => this.toNumber(d.x) <= 0.05);
    }
    else if (type === 'gt50') {
      filteredData = filteredData.filter((d: { x: number; }) => this.toNumber(d.x) > 0.05);
    }

    this.chartData = filteredData;
    if (!this.chartData?.length) {
      this.hasError = true;
      this.errorMessage = 'No data found for the selected filters.';
      this.myChart?.clear();
      this.myChart?.hideLoading();
      return;
    }

    this.hasError = false;
    this.renderChart();
  }


  private renderChart(): void {
    const option = {
      backgroundColor: '#F3F4F6',
      grid: {
        left: '6%',
        bottom: '20%',
        top: '5%'
      },
      tooltip: {
        show: true,
        trigger: 'item',
        confine: true,
        enterable: true,
        formatter: (params: any) => {
          const id = "mini-" + Math.random().toString(36).substring(2);
          const pointData = params?.data?.raw || params?.data || {};
          const label = params?.data?.label || params?.data?.value?.[2] || this.getPointLabel(pointData);
          const xValue = Array.isArray(params?.value) ? params.value[0] : pointData?.x;
          const yValue = Array.isArray(params?.value) ? params.value[1] : pointData?.y;
          const hasPopupData =
            Array.isArray(pointData?.weightAndScorePopup) &&
            pointData.weightAndScorePopup.length > 0;
          const xMovementValue = pointData?.data?.['x-axis movement'];
          const yMovementValue = pointData?.data?.['y-axis movement'];
          const chartHeight = hasPopupData ? '430px' : '56vh';
          const maxChartHeight = hasPopupData ? '430px' : '560px';

          setTimeout(() => {
            this.renderMiniChart(id, pointData, label);
          }, 0);

          return `
      <div style="width:72vw; min-width:900px; max-width:1160px;">

        <div style="font-size:1.1rem; font-weight:700; margin-bottom:8px; line-height:1.2;">
          ${this.escapeHtml(label)}
        </div>

        <div style="display:flex; gap:10px; margin-bottom:8px;">
          <div style="flex:1; min-height:64px; background-color:#f0f0f0; border:1.5px solid #626060; border-radius:10px; box-shadow:0 2px 6px rgba(0, 0, 0, 0.04); padding:8px; display:flex; align-items:center; justify-content:center;">
            <div style="display:flex; flex-direction:column; align-items:center; text-align:center; gap:4px; width:100%;">
              <div style="font-size:1rem; font-weight:600; color:#333;">${this.escapeHtml(this.data.xAxis || 'Spend ($M)')}</div>
              <div style="font-size:1.45rem; font-weight:600; color:#000; line-height:1;">${this.formatNumber(xValue, 2)}</div>
            </div>
          </div>
          <div style="flex:1; min-height:64px; background-color:#f0f0f0; border:1.5px solid #626060; border-radius:10px; box-shadow:0 2px 6px rgba(0, 0, 0, 0.04); padding:8px; display:flex; align-items:center; justify-content:center;">
            <div style="display:flex; flex-direction:column; align-items:center; text-align:center; gap:4px; width:100%;">
              <div style="font-size:1rem; font-weight:600; color:#333;">${this.escapeHtml(this.data.yAxis || 'Spend Effectiveness')}</div>
              <div style="font-size:1.45rem; font-weight:600; color:#000; line-height:1;">${this.formatNumber(yValue, 4)}</div>
            </div>
          </div>
        </div>

        <div style="display:flex; gap:10px; height:${chartHeight}; max-height:${maxChartHeight}; min-height:430px; align-items:stretch;">
          ${hasPopupData ? `
          <div style="width:210px; height:100%; display:flex; flex-direction:column; gap:10px;">
            <div style="flex:1; min-height:0; background-color:#f0f0f0; border:1.5px solid #626060; border-radius:10px; box-shadow:0 2px 6px rgba(0, 0, 0, 0.04); padding:8px; display:flex; align-items:center; justify-content:center;">
              <div style="display:flex; flex-direction:column; align-items:center; text-align:center; gap:6px; width:100%;">
                <div style="font-size:1rem; font-weight:600; color:#333;">X-axis movement</div>
                <div style="font-size:0.9rem; font-weight:500; color:#333;">(Product fit change)</div>
                <div style="font-size:1.35rem; font-weight:600; color:${this.getMovementColor(xMovementValue)}; line-height:1;">${this.formatSignedNumber(xMovementValue, 2)}</div>
              </div>
            </div>
            <div style="flex:1; min-height:0; background-color:#f0f0f0; border:1.5px solid #626060; border-radius:10px; box-shadow:0 2px 6px rgba(0, 0, 0, 0.04); padding:8px; display:flex; align-items:center; justify-content:center;">
              <div style="display:flex; flex-direction:column; align-items:center; text-align:center; gap:6px; width:100%;">
                <div style="font-size:1rem; font-weight:600; color:#333;">Y-axis movement</div>
                <div style="font-size:0.9rem; font-weight:500; color:#333;">(Financial & Strategic change)</div>
                <div style="font-size:1.35rem; font-weight:600; color:${this.getMovementColor(yMovementValue)}; line-height:1;">${this.formatSignedNumber(yMovementValue, 2)}</div>
              </div>
            </div>
          </div>` : ''}

          <div id="${id}"
               style="flex:1; min-width:0; height:100%; border:1px solid #ccc; border-radius:4px; background:#fff; overflow:hidden;">
          </div>
        </div>
      </div>
    `;
        }
      },
      xAxis: {
        name: this.data.xAxis,
        type: 'value',
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: {
          fontWeight: 600,
          fontSize: 14,
        },
      },
      yAxis: {
        name: this.data.yAxis,
        scale: true,
        nameLocation: 'middle',
        nameGap: 50,
        nameTextStyle: {
          fontWeight: 600,
          fontSize: 14,
        },
      },
      dataZoom: [
        { type: 'inside', xAxisIndex: 0, zoomSpeed: 0.2, },
        { type: 'inside', yAxisIndex: 0, zoomSpeed: 0.2, },
      ],
      series: [
        {
          symbolSize: 10,
          data: this.chartData.map((p: any) => ({
            value: [this.toNumber(p.x), this.toNumber(p.y), this.getPointLabel(p)],
            label: this.getPointLabel(p),
            raw: p,
          })),
          type: 'scatter',
          label: {
            show: true,
            position: 'top',
            fontSize: 10,
            fontWeight: 'bold',
            formatter: (params: any) => {
              let name = params?.data?.label || params?.data?.value?.[2] || '';
              const replacements: [RegExp, string][] = [
                [/Mining/gi, 'MIN'],
                [/Marine/gi, 'MX'],
                [/QSK/gi, 'Q'],
                [/OIL & GAS/gi, 'OG'],
                [/ GAS/gi, 'G'],
                [/O&G/gi, 'OG'],
                [/Defense/gi, ''],
                [/QST/gi, 'Q'],
                [/Rail/gi, 'Rail']
              ];
              for (const [pattern, replacement] of replacements) {
                name = name.replace(pattern, replacement);
              }
              return name.replace(/\s+/g, ' ').trim();
            },
          },
          itemStyle: {
            color: 'transparent',
            borderColor: '#d9291e',
            borderWidth: 2,
          },

          labelLayout: { hideOverlap: true, dy: 5 },

        },
      ],
    };

    this.myChart.setOption(option);
    setTimeout(() => this.myChart.hideLoading(), 500);
  }

  private renderMiniChart(id: string, pointData: any, label: string): void {
    const el = document.getElementById(id);
    if (!el) return;

    const existingInstance = echarts.getInstanceByDom(el);
    if (existingInstance) {
      existingInstance.dispose();
    }

    const miniChart = echarts.init(el);
    const rawPopupData = Array.isArray(pointData?.weightAndScorePopup)
      ? pointData.weightAndScorePopup
      : [];
      
    // Deduplicate by quarter_year, keeping the last occurrence to ensure the latest score is used
    const uniquePopupData: any[] = [];
    const seen = new Set<string>();
    for (let i = rawPopupData.length - 1; i >= 0; i--) {
      const qy = rawPopupData[i]?.quarter_year;
      if (qy && !seen.has(qy)) {
        seen.add(qy);
        uniquePopupData.unshift(rawPopupData[i]);
      } else if (!qy) {
        uniquePopupData.unshift(rawPopupData[i]);
      }
    }
    const popupData = uniquePopupData;
    this.debugLog('Hover pointData', pointData);
    this.debugLog('Hover weightAndScorePopup', popupData);
    if (popupData.length) {
      const option = this.buildPopupBreakdownOption(pointData, popupData);
      miniChart.setOption(option);
      return;
    }

    this.renderLegacyMiniChart(miniChart, label);
  }

  private buildPopupBreakdownOption(pointData: any, popupData: any[]) {
    const quarterLabels = popupData.map((item: any) =>
      this.formatQuarterLabel(item?.quarter_year)
    );
    const xAxisBreakdown = this.buildAxisBreakdown(popupData, 'x');
    const yAxisBreakdown = this.buildAxisBreakdown(popupData, 'y');
    const xMovement = pointData?.data?.['x-axis movement'];
    const yMovement = pointData?.data?.['y-axis movement'];
    this.debugLog('Popup movement values', { xMovement, yMovement });
    const spend = pointData?.x;
    const xAxisMax = this.getAxisMaxValue(xAxisBreakdown);
    const yAxisMax = this.getAxisMaxValue(yAxisBreakdown);

    const xSeries = this.createStackedSeries(
      xAxisBreakdown,
      0,
      0,
      'xStack'
    );
    const ySeries = this.createStackedSeries(
      yAxisBreakdown,
      1,
      1,
      'yStack'
    );
    const popupFactorColors = this.buildPopupFactorColorMap([
      ...xAxisBreakdown.factorNames,
      ...yAxisBreakdown.factorNames,
    ]);

    const option = {
      backgroundColor: '#ffffff',
      animation: false,
      tooltip: { show: false },
      legend: [
        {
          orient: 'vertical',
          top: '8%',
          left: '70%',
          width: 200,
          height: '38%',
          align: 'left',
          itemGap: 4,
          itemWidth: 10,
          itemHeight: 10,
          formatter: (name: string) => this.wrapLegendText(name, 20),
          textStyle: { color: '#333', fontSize: 11, lineHeight: 14 },
          data: xAxisBreakdown.factorNames,
        },
        {
          orient: 'vertical',
          top: '52%',
          left: '70%',
          width: 200,
          height: '38%',
          align: 'left',
          itemGap: 4,
          itemWidth: 10,
          itemHeight: 10,
          formatter: (name: string) => this.wrapLegendText(name, 20),
          textStyle: { color: '#333', fontSize: 11, lineHeight: 14 },
          data: yAxisBreakdown.factorNames,
        },
      ],
      grid: [
        { left: 32, right: 250, top: '8%', height: '38%', containLabel: true },
        { left: 32, right: 250, top: '52%', height: '38%', containLabel: true },
      ],
      xAxis: [
        {
          type: 'value',
          gridIndex: 0,
          max: xAxisMax,
          splitLine: { show: false },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { show: false },
        },
        {
          type: 'value',
          gridIndex: 1,
          max: yAxisMax,
          splitLine: { show: false },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { show: false },
        },
      ],
      yAxis: [
        {
          type: 'category',
          inverse: true,
          data: quarterLabels,
          gridIndex: 0,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#666',
            fontSize: 12,
          },
        },
        {
          type: 'category',
          inverse: true,
          data: quarterLabels,
          gridIndex: 1,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#666',
            fontSize: 12,
          },
        },
      ],
      series: [
        ...xSeries.map((series: any) => ({
          ...series,
          itemStyle: {
            color: popupFactorColors[series.name],
          },
        })),
        this.createTotalSeries('xTotal', xAxisBreakdown.totals, 0, 0, 'xStack'),
        ...ySeries.map((series: any) => ({
          ...series,
          itemStyle: {
            color: popupFactorColors[series.name],
          },
        })),
        this.createTotalSeries('yTotal', yAxisBreakdown.totals, 1, 1, 'yStack'),
      ],
      graphic: [
        {
          type: 'line',
          left: 32,
          right: 250,
          top: '49.5%',
          shape: { x1: 0, y1: 0, x2: 680, y2: 0 },
          style: { stroke: '#d0d0d0', lineWidth: 1 },
          silent: true,
        },
      ],
    };

    return option;
  }

  private buildPopupFactorColorMap(factorNames: string[]): Record<string, string> {
    const colorMap: Record<string, string> = {};
    const usedColors = new Set<string>();
    const uniqueFactors = Array.from(new Set(factorNames.filter(Boolean)));
    const seedPalette = [
      ...this.fallbackPalette,
      '#8b5cf6',
      '#10b981',
      '#f59e0b',
      '#ef4444',
      '#06b6d4',
      '#a855f7',
      '#84cc16',
      '#f97316',
    ];

    uniqueFactors.forEach((factorName, index) => {
      const preferredColor = this.factorColorMap[factorName];
      if (preferredColor && !usedColors.has(preferredColor)) {
        colorMap[factorName] = preferredColor;
        usedColors.add(preferredColor);
        return;
      }

      const nextSeedColor = seedPalette.find((color) => !usedColors.has(color));
      if (nextSeedColor) {
        colorMap[factorName] = nextSeedColor;
        usedColors.add(nextSeedColor);
        return;
      }

      const hue = (index * 137.508) % 360;
      const generatedColor = `hsl(${hue.toFixed(0)}, 65%, 45%)`;
      colorMap[factorName] = generatedColor;
      usedColors.add(generatedColor);
    });

    return colorMap;
  }

  private wrapLegendText(label: string, maxCharsPerLine = 18): string {
    const text = String(label || '').trim();
    if (!text) return '';
    if (text.length <= maxCharsPerLine) return text;

    const words = text.split(/\s+/);
    const lines: string[] = [];
    let currentLine = '';

    words.forEach((word) => {
      if (!currentLine) {
        currentLine = word;
        return;
      }

      const candidate = `${currentLine} ${word}`;
      if (candidate.length <= maxCharsPerLine) {
        currentLine = candidate;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    });

    if (currentLine) lines.push(currentLine);
    return lines.join('\n');
  }

  private buildAxisBreakdown(popupData: any[], axisKey: AxisKey): AxisBreakdown {
    const quarterLabels = popupData.map((item: any) =>
      this.formatQuarterLabel(item?.quarter_year)
    );
    const factorNames: string[] = [];

    popupData.forEach((quarter: any) => {
      const factors = quarter?.[axisKey]?.factors;
      if (!Array.isArray(factors)) return;
      factors.forEach((factorObj: any) => {
        const factorName = Object.keys(factorObj || {})[0];
        if (factorName && !factorNames.includes(factorName)) {
          factorNames.push(factorName);
        }
      });
    });

    const valuesByFactor = factorNames.map((factorName) =>
      popupData.map((quarter: any) =>
        this.getFactorValue(quarter?.[axisKey]?.factors, factorName)
      )
    );
    const totals = popupData.map((quarter: any) =>
      this.toNumber(quarter?.[axisKey]?.total)
    );

    return { quarterLabels, factorNames, valuesByFactor, totals };
  }

  private getFactorValue(factors: any[], factorName: string): number {
    if (!Array.isArray(factors)) return 0;
    const factor = factors.find((f: any) =>
      Object.prototype.hasOwnProperty.call(f || {}, factorName)
    );
    return this.toNumber(factor?.[factorName]);
  }

  private createStackedSeries(
    axisBreakdown: AxisBreakdown,
    xAxisIndex: number,
    yAxisIndex: number,
    stack: string
  ): any[] {
    return axisBreakdown.factorNames.map((factorName, index) => ({
      name: factorName,
      type: 'bar',
      stack,
      barWidth: 18,
      xAxisIndex,
      yAxisIndex,
      label: {
        show: false,
        formatter: (params: any) =>
          this.toNumber(params.value) > 0 ? this.formatNumber(params.value, 1) : '',
      },
      emphasis: { focus: 'series' },
      itemStyle: {
        color:
          this.factorColorMap[factorName] ||
          this.fallbackPalette[index % this.fallbackPalette.length],
      },
      data: (axisBreakdown.valuesByFactor[index] || []).map((rawValue: number) => {
        const value = this.toNumber(rawValue);
        if (value <= 0) return 0;

        // Move only very small values above the bar to keep tiny labels readable.
        const isSmallSegment = value < 0.1;
        return {
          value,
          label: {
            show: true,
            position: isSmallSegment ? 'top' : 'inside',
            color: isSmallSegment ? '#222' : '#fff',
            fontSize: isSmallSegment ? 10 : 11,
            distance: isSmallSegment ? 4 : 0,
            formatter: this.formatNumber(value, 1),
          },
        };
      }),
    }));
  }

  private createTotalSeries(
    name: string,
    totals: number[],
    xAxisIndex: number,
    yAxisIndex: number,
    stack: string
  ) {
    return {
      name,
      type: 'bar',
      stack,
      barWidth: 18,
      xAxisIndex,
      yAxisIndex,
      itemStyle: {
        color: 'rgba(0,0,0,0)',
      },
      label: {
        show: true,
        position: 'right',
        color: '#222',
        fontSize: 18,
        fontWeight: 'bold',
        formatter: (params: any) => this.formatNumber(totals[params.dataIndex], 2),
      },
      tooltip: { show: false },
      emphasis: { disabled: true },
      data: totals.map(() => 0),
    };
  }

  private getAxisMaxValue(axisBreakdown: AxisBreakdown): number {
    const flatValues = [
      ...axisBreakdown.totals,
      ...axisBreakdown.valuesByFactor.flat(),
    ].map((value) => this.toNumber(value));

    const maxValue = flatValues.length ? Math.max(...flatValues) : 0;
    if (maxValue <= 0) return 1;
    return Number((maxValue * 1.2).toFixed(2));
  }

  private renderLegacyMiniChart(miniChart: echarts.ECharts, label: string): void {
    const rawdata: any[] = Array.isArray(this.data?.rawData) ? this.data.rawData : [];
    const model = label.split(" ").pop();
    const data = rawdata.filter((item: any) => {
      const modelMatches = item.model == model;
      return modelMatches;
    });
    if (!data.length) {
      miniChart.clear();
      return;
    }

    const createPlannedSeries = (
      metric: string,
      xAxisIndex: number,
      yAxisIndex: number,
      gridIndex: number
    ) => ({
      name: `Planned`,
      type: 'custom',
      xAxisIndex,
      yAxisIndex,
      gridIndex,
      z: 1000,
      renderItem: (params: any, api: any) => {
        const yIndex = api.value(0);
        const plannedValue = api.value(1);
        const actualValue = api.value(2);

        const coordPlanned = api.coord([plannedValue, yIndex]);

        const barWidth = 20; // Must match barWidth in actual series
        const barCenterOffset = barWidth / 2;

        const plannedLineColor = '#444343cc';

        const labelOffsetX = plannedValue > actualValue ? 5 : -25;

        return {
          type: 'group',
          children: [
            // Vertical dotted line
            {
              type: 'line',
              shape: {
                x1: coordPlanned[0],
                y1: coordPlanned[1] - barCenterOffset,
                x2: coordPlanned[0],
                y2: coordPlanned[1] + barCenterOffset,
              },
              style: {
                stroke: plannedLineColor,
                lineWidth: 2,
                lineDash: [4, 4],
              },
              z: 1000,
            },
            // Label
            {
              type: 'text',
              style: {
                text: `${plannedValue}%`,
                x: coordPlanned[0] + 5, // offset slightly to avoid overlap
                y: coordPlanned[1], // center vertically
                fill: plannedLineColor,
                font: '12px sans-serif',
                align: 'left', // ensures horizontal alignment
                verticalAlign: 'middle', // ensures vertical centering
              },
              z: 1000,
            },
          ],
        };
      },
      data: data.map((d: any, i: any) => [i, d.planned[metric], d.actual[metric]]),
      silent: true,
    });
    // Compute dynamic grid height based on number of Y-axis items
    const rowHeight = 40; // pixels per Y-axis interval (adjust as desired)
    const numRows = data.length;
    const gridHeight = numRows * rowHeight;

    // Also resize the chart container to fit (optional but recommended)
    const chartContainer = document.getElementById('chart');
    if (chartContainer) {
      chartContainer.style.height = `${gridHeight + 150}px`; // +150 for padding/titles
    }
    const option = {
      grid: [
        { left: '8%', top: 30, width: '22%', height: gridHeight }, // CT
        { left: '31%', top: 30, width: '22%', height: gridHeight }, // Sourcing
        { left: '54%', top: 30, width: '22%', height: gridHeight }, // Quality
        { left: '77%', top: 30, width: '22%', height: gridHeight }, // Mfg
      ],
      title: [
        {
          text: 'Customer Tailoring',
          left: '16%',
          top: -5,
          textAlign: 'center',
          textStyle: { fontSize: 14, fontWeight: 'bold' },
        },
        {
          text: 'Sourcing',
          left: '39%',
          top: -5,
          textAlign: 'center',
          textStyle: { fontSize: 14, fontWeight: 'bold' },
        },
        {
          text: 'Quality',
          left: '62%',
          top: -5,
          textAlign: 'center',
          textStyle: { fontSize: 14, fontWeight: 'bold' },
        },
        {
          text: 'Manufacturing',
          left: '85%',
          top: -5,
          textAlign: 'center',
          textStyle: { fontSize: 14, fontWeight: 'bold' },
        },
      ],
      xAxis: [
        {
          type: 'value',
          name: 'CT',
          gridIndex: 0,
          max: 100,
          show: false,
        },
        {
          type: 'value',
          name: 'Sourcing',
          gridIndex: 1,
          max: 100,
          show: false,
        },
        { type: 'value', name: 'Quality', gridIndex: 2, max: 100, show: false },
        { type: 'value', name: 'Mfg', gridIndex: 3, max: 100, show: false },
      ],
      yAxis: [
        {
          type: 'category',
          data: data.map((d: { model: any; }) => d.model),
          gridIndex: 0,
          show: true, // must be true for split lines
          axisTick: { show: false }, // hide ticks
          axisLabel: {
            show: true,
            fontWeight: 'bold',
            fontSize: 12,
            color: '#000',
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#ccc',
              width: 1,
              type: 'solid',
            },
          },
        },
        {
          type: 'category',
          data: data.map((d: { model: any; }) => d.model),
          gridIndex: 1,
          show: true,
          axisLabel: { show: false },
          axisTick: { show: false },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#ccc',
              width: 1,
              type: 'solid',
            },
          },
        },
        {
          type: 'category',
          data: data.map((d: { model: any; }) => d.model),
          gridIndex: 2,
          show: true,
          axisLabel: { show: false },
          axisTick: { show: false },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#ccc',
              width: 1,
              type: 'solid',
            },
          },
        },
        {
          type: 'category',
          data: data.map((d: { model: any; }) => d.model),
          gridIndex: 3,
          show: true,
          axisLabel: { show: false },
          axisTick: { show: false },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#ccc',
              width: 1,
              type: 'solid',
            },
          },
        },
      ],
      series: [
        {
          name: 'CT Actual',
          type: 'bar',
          data: data.map((d: { actual: { ct: any; }; }) => d.actual.ct),
          xAxisIndex: 0,
          yAxisIndex: 0,
          itemStyle: {
            color: (params: any) =>
              this.typeColors[data[params.dataIndex].type] || '#3BA272',
          },
          barWidth: 20,
          barCategoryGap: 20,
          label: {
            show: true,
            position: 'inside',
            formatter: (params: any) => `${params.value}%`,
            color: '#fff',
          },
        },
        {
          name: 'Sourcing Actual',
          type: 'bar',
          data: data.map((d: { actual: { sourcing: any; }; }) => d.actual.sourcing),
          xAxisIndex: 1,
          yAxisIndex: 1,
          itemStyle: {
            color: (params: any) =>
              this.typeColors[data[params.dataIndex].type] || '#3BA272',
          },
          barWidth: 20,
          label: {
            show: true,
            position: 'inside',
            formatter: (params: any) => `${params.value}%`,
            color: '#fff',
          },
        },
        {
          name: 'Quality Actual',
          type: 'bar',
          data: data.map((d: { actual: { quality: any; }; }) => d.actual.quality),
          xAxisIndex: 2,
          yAxisIndex: 2,
          itemStyle: {
            color: (params: any) =>
              this.typeColors[data[params.dataIndex].type] || '#3BA272',
          },
          barWidth: 20,
          label: {
            show: true,
            position: 'inside',
            formatter: (params: any) => `${params.value}%`,
            color: '#fff',
          },
        },
        {
          name: 'Mfg Actual',
          type: 'bar',
          data: data.map((d: { actual: { mfg: any; }; }) => d.actual.mfg),
          xAxisIndex: 3,
          yAxisIndex: 3,
          itemStyle: {
            color: (params: any) =>
              this.typeColors[data[params.dataIndex].type] || '#3BA272',
          },
          barWidth: 20,
          label: {
            show: true,
            position: 'inside',
            formatter: (params: any) => `${params.value}%`,
            color: '#fff',
          },
        },

        { ...createPlannedSeries('ct', 0, 0, 0) },
        { ...createPlannedSeries('sourcing', 1, 1, 1) },
        { ...createPlannedSeries('quality', 2, 2, 2) },
        { ...createPlannedSeries('mfg', 3, 3, 3) },
      ],
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          const dataIndex = params[0].dataIndex;
          const gridIndex = params[0].axisIndex;
          const d: any = data[dataIndex];

          const metricMap = ['ct', 'sourcing', 'quality', 'mfg'];
          const metric = metricMap[gridIndex] || 'ct';

          return `
      <b>${d.model}</b><br/>
      Actual: ${d.actual[metric]}%<br/>
      Planned: ${d.planned[metric]}%
    `;
        },
      },
    };

    miniChart.setOption(option);
  }

  private getPointLabel(item: any): string {
    return (item?.label || item?.product_id || '').toString();
  }

  private includesIgnoreCase(source: string, search: string): boolean {
    return (source || '').toUpperCase().includes((search || '').toUpperCase());
  }

  private toNumber(value: any): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private formatNumber(value: any, precision = 2): string {
    const parsed = this.toNumber(value);
    return Number(parsed.toFixed(precision)).toString();
  }

  private formatSignedNumber(value: any, precision = 2): string {
    const parsed = this.toNumber(value);
    const formatted = this.formatNumber(parsed, precision);
    if (parsed > 0) return `+${formatted}`;
    return formatted;
  }

  private getMovementColor(value: any): string {
    const parsed = this.toNumber(value);
    if (parsed > 0) return '#2e7d32';
    if (parsed < 0) return '#d9291e';
    return '#000';
  }

  private formatQuarterLabel(value: string): string {
    if (!value) return '';
    const normalized = String(value).replace(/\s+/g, '');
    const match = normalized.match(/^Q([1-4])(\d{2}|\d{4})$/i);
    if (match) {
      const quarter = match[1];
      const year = match[2];
      const yearLabel = year.length === 4 ? year.slice(-2) : year;
      return `Q${quarter} ${yearLabel}`;
    }
    return String(value);
  }

  private escapeHtml(value: string): string {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  typeColors: Record<string, string> = {
    'Growth': '#98d34f', // Green
    'Core/Niche': '#7eccfaff', // Blue
    'Harvest/Transition': '#d6d6d6', // Orange
  };

  typeKeys(): string[] {
    return Object.keys(this.typeColors);
  }
  private addResizeListener(): void {
    window.removeEventListener('resize', this.resizeChart);
    window.addEventListener('resize', this.resizeChart);
  }
  private resizeChart = () => {
    this.myChart?.resize();
  };

  ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
    this.myChart?.dispose();
    window.removeEventListener('resize', this.resizeChart);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
